import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Wifi, MapPin, Users, ShieldCheck, Wrench, ArrowRight, CheckCircle2, Zap } from "lucide-react";
import BrandLogo from "@/components/app/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";

const LOCATIONS = ["Margao", "Panjim", "Dharbandora", "Chandor", "Sanguem"];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user !== false) {
      const map = { admin: "/admin", technician: "/tech", customer: "/portal" };
      navigate(map[user.role] || "/portal");
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#05070d] text-slate-100 overflow-hidden">
      <header className="border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo height={64} />
            <div className="hidden sm:block leading-tight border-l border-slate-800 pl-3">
              <div className="font-display font-extrabold text-xl tracking-tight text-purple-300">GOA</div>
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Complain Center</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button data-testid="header-login-btn" className="bg-purple-500 hover:bg-purple-600">Staff sign in</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/40 to-slate-950" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs font-mono-alt uppercase tracking-widest text-emerald-300">
              <span className="live-dot" /> Live Dispatch • 5 Goa hubs online
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Broadband complaints, <span className="bg-gradient-to-r from-purple-400 via-fuchsia-300 to-purple-200 bg-clip-text text-transparent">resolved in real-time</span> across Goa.
            </h1>
            <p className="text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
              Internal staff console for NV Skynet Goa — office staff raise complaints on behalf of subscribers, dispatch to the right technician, and field teams close the loop across Margao, Panjim, Dharbandora, Chandor and Sanguem.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/login"><Button data-testid="hero-cta-signin" size="lg" className="bg-purple-500 hover:bg-purple-600 h-12 px-6 text-base"><Zap className="w-4 h-4 mr-2" /> Staff sign in <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            </div>
            <div className="flex flex-wrap gap-2 pt-4">
              {LOCATIONS.map((l) => (
                <div key={l} className="px-3 py-1.5 rounded-full glass text-xs text-slate-300 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-purple-400" /> {l}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-purple-500/30 to-fuchsia-400/10 blur-2xl rounded-3xl" />
              <div className="relative tactical-card p-1 rounded-2xl">
                <img
                  src="https://images.unsplash.com/photo-1594915440248-1e419eba6611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxmaWJlciUyMG9wdGljJTIwdGVjaG5pY2lhbiUyMGludGVybmV0JTIwcmVwYWlyJTIwbmV0d29yayUyMGVuZ2luZWVyfGVufDB8fHx8MTc5MDc2MDIwMnww&ixlib=rb-4.1.0&q=85"
                  alt="Fiber optic rack"
                  className="w-full h-[420px] object-cover rounded-xl opacity-90"
                />
                <div className="absolute bottom-4 left-4 right-4 tactical-card p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-400" /></div>
                  <div>
                    <div className="text-sm font-semibold text-white">Avg resolution 4.2 hrs</div>
                    <div className="text-xs text-slate-400 font-mono-alt">Live across all 5 hubs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: <Users className="w-6 h-6 text-purple-400" />, title: "Office staff console", body: "Raise complaints on behalf of subscribers with full customer details, account ID, area and photos — takes 30 seconds." },
            { icon: <Wrench className="w-6 h-6 text-amber-400" />, title: "Field technician app", body: "Every technician sees only their assigned tickets, marks progress, resolves and closes issues with notes — mobile ready." },
            { icon: <ShieldCheck className="w-6 h-6 text-emerald-400" />, title: "Admin dispatch", body: "Filter by area, assign to the right team group, watch SLA analytics and technician performance in one console." },
          ].map((f) => (
            <div key={f.title} className="tactical-card p-6">
              <div className="w-12 h-12 rounded-lg bg-slate-800/70 border border-slate-700 flex items-center justify-center mb-4">{f.icon}</div>
              <h3 className="font-display text-xl font-bold text-white mb-1.5">{f.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800/60 py-8 mt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Wifi className="w-4 h-4" /> NV Skynet Goa · Complain Center</div>
          <div className="text-xs font-mono-alt text-slate-600 uppercase tracking-widest">v1 · Real-time build</div>
        </div>
      </footer>
    </div>
  );
}
